export const BOOKING_SUBMISSION_POLICY = Object.freeze({
  SUCCESS: 'SUCCESS',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  RETRYABLE_PRECHECK: 'RETRYABLE_PRECHECK',
  RESTART_SAFE: 'RESTART_SAFE',
  MANUAL_REVIEW: 'MANUAL_REVIEW',
});

export const BOOKING_SUBMISSION_BLOCK_VERSION = 2;
export const DEFAULT_SAFE_RESTART_TTL_MS = 60 * 60 * 1000;

const normalizeToken = value => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

const normalizeOptionalBoolean = value => {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return null;
};

const firstDefined = (...values) => values.find(value => (
  value !== undefined
  && value !== null
  && !(typeof value === 'string' && value.trim() === '')
));

const MANUAL_REVIEW_OUTCOMES = new Set([
  'unknown',
  'outcome_unknown',
  'uncertain',
  'manual_review',
  'manual_review_required',
]);

const PRECHECK_OUTCOMES = new Set([
  'not_attempted',
  'precheck_failed',
  'preflight_failed',
  'validation_failed',
  'auth_required',
]);

const RESTART_OUTCOMES = new Set([
  'confirmed_failed',
  'confirmed_failure',
  'failed_confirmed',
]);

const MANUAL_REVIEW_RETRY_POLICIES = new Set([
  'manual_review',
  'manual_review_required',
  'blocked',
  'do_not_retry',
  'no_retry',
]);

const RETRYABLE_RETRY_POLICIES = new Set([
  'retry',
  'retry_same_session',
  'same_session',
  'correct_and_retry',
  'retry_current_request',
  'refresh_booking_session',
]);

const RESTART_RETRY_POLICIES = new Set([
  'restart',
  'restart_flow',
  'new_session',
  'restart_from_beginning',
]);

const AUTH_RETRY_POLICIES = new Set([
  'auth_required',
  'login_required',
  'login',
]);

export function getTraveltekBookingSignals(response = {}) {
  const traveltekResponse = response?.traveltek_response;
  const traveltekResults = Array.isArray(traveltekResponse?.results)
    ? traveltekResponse.results
    : [];
  const statuses = [];
  const primaryStatuses = [];
  const handoffStatuses = [];
  const appendStatus = (target, source) => {
    const value = normalizeToken(firstDefined(
      source?.status,
      source?.bookingstatus,
      source?.bookingStatus,
    ));
    if (value) target.push(value);
  };

  // 백엔드 판정과 동일하게 첫 result만 보지 않고 모든 result/item을 확인한다.
  traveltekResults.forEach((result, resultIndex) => {
    if (!result || typeof result !== 'object') return;
    appendStatus(statuses, result);
    if (resultIndex === 0) appendStatus(primaryStatuses, result);
    const handoffStatus = normalizeToken(firstDefined(
      result?.handoffstatus,
      result?.handoffStatus,
      result?.handoff_status,
    ));
    if (handoffStatus) handoffStatuses.push(handoffStatus);

    const bookingDetails = result?.bookingdetails;
    const bookingItems = Array.isArray(bookingDetails?.bookingitems)
      ? bookingDetails.bookingitems
      : [];
    bookingItems.forEach((item, itemIndex) => {
      appendStatus(statuses, item);
      appendStatus(statuses, item?.cruisedetail);
      if (resultIndex === 0 && itemIndex === 0) {
        appendStatus(primaryStatuses, item);
        appendStatus(primaryStatuses, item?.cruisedetail);
      }
    });
  });

  const hasAnySuccessStatus = statuses.some(status => ['booked', 'on_request'].includes(status));
  const hasSuccessStatus = primaryStatuses.some(status => ['booked', 'on_request'].includes(status));
  const hasFailedStatus = statuses.includes('failed') || handoffStatuses.includes('failed');
  const successFlag = response?.success === true;
  // 로컬 저장도 results[0]을 기준으로 하므로 첫 Traveltek 결과에
  // Booked/On-Request가 실제로 있어야 다음 단계로 진행한다.
  const isSuccessfulStatus = hasSuccessStatus && !hasFailedStatus;

  return {
    traveltekResponse,
    traveltekResults,
    statuses,
    primaryStatuses,
    handoffStatuses,
    hasAnySuccessStatus,
    hasResults: traveltekResults.some(result => result && typeof result === 'object'),
    hasFailedStatus,
    successFlag,
    canProceedWithBooking: successFlag
      && traveltekResults.some(result => result && typeof result === 'object')
      && isSuccessfulStatus,
  };
}

const hasBookingEvidence = (value, includeStatus = true) => {
  if (!value || typeof value !== 'object') return false;
  return Object.entries(value).some(([key, item]) => {
    const name = key.toLowerCase().replace(/_/g, '');
    if (['bookingid', 'portfolioid', 'bookingreference', 'supplierbookingreference',
      'reservation', 'reservationreference', 'reservationnumber', 'reservationno', 'reservationidx', 'reservationcode'].includes(name)
      && item != null && String(item).trim() !== '' && String(item) !== '0') return true;
    if (includeStatus && ['status', 'bookingstatus'].includes(name)
      && ['booked', 'on_request', 'pending', 'processing'].includes(normalizeToken(item))) return true;
    return hasBookingEvidence(item, includeStatus);
  });
};

const isMissingIdentifiersRecovery = response => response?.recovery_code === 'MISSING_BOOKING_IDENTIFIERS'
  && response.booking_failure_confirmed === true && response.booking_identifiers_missing === true;

const hasCheckedRecovery = response => response?.booking_history_checked === true
  && typeof response?.recovery_session_key === 'string' && response.recovery_session_key.trim() !== ''
  && !hasBookingEvidence(response, !isMissingIdentifiersRecovery(response));

export const isAutomaticBookingRecovery = (response, sessionKey) => hasCheckedRecovery(response)
  && ((response.auto_restart_booking_flow === true
    && ['CABIN_HOLD_UNAVAILABLE', 'SENIOR_FARE_REJECTED', 'MISSING_BOOKING_IDENTIFIERS'].includes(response.recovery_code))
    || (response.recovery_code === 'CABIN_RESELECT_REQUIRED' && response.reselect_cabin === true))
  && response.recovery_session_key === sessionKey;

/**
 * create_reservation.php 응답을 UI 행동으로 변환한다.
 * 명시적으로 안전하다고 확인되지 않은 조합은 항상 MANUAL_REVIEW로 닫는다.
 */
export function classifyBookingSubmissionResponse({
  response = {},
  httpStatus = null,
  transportError = false,
  expectedSessionKey = null,
} = {}) {
  const signals = getTraveltekBookingSignals(response);
  const normalizedHttpStatus = Number.isFinite(Number(httpStatus)) ? Number(httpStatus) : null;
  const bookingAttempted = normalizeOptionalBoolean(firstDefined(
    response?.booking_attempted,
    response?.bookingAttempted,
    response?.book_attempted,
    response?.bookAttempted,
  ));
  const bookingOutcome = normalizeToken(firstDefined(
    response?.booking_outcome,
    response?.bookingOutcome,
    response?.outcome,
  ));
  const retryPolicy = normalizeToken(firstDefined(
    response?.retry_policy,
    response?.retryPolicy,
  ));
  const manualReviewRequired = response?.manual_review_required === true
    || response?.manualReviewRequired === true;
  const outcomeUnknown = response?.outcome_unknown === true
    || response?.outcomeUnknown === true
    || MANUAL_REVIEW_OUTCOMES.has(bookingOutcome);
  const resetRequested = response?.reset_booking_flow === true
    || response?.resetBookingFlow === true
    || response?.safe_to_restart === true
    || response?.safeToRestart === true
    || RESTART_RETRY_POLICIES.has(retryPolicy);

  const safeHoldRejection = hasCheckedRecovery(response)
    && response.recovery_code === 'CABIN_HOLD_UNAVAILABLE'
    && response.auto_restart_booking_flow === true
    && bookingAttempted === false && bookingOutcome === 'not_attempted'
    && retryPolicy === 'restart_from_beginning';

  const safeCabinReselection = hasCheckedRecovery(response)
    && response.recovery_code === 'CABIN_RESELECT_REQUIRED'
    && response.reselect_cabin === true && response.success === false
    && bookingAttempted === false && bookingOutcome === 'not_attempted'
    && retryPolicy === 'refresh_booking_session' && !resetRequested
    && response.auto_restart_booking_flow === false;

  const invalidRecovery = response?.recovery_code && (
    !hasCheckedRecovery(response)
    || (httpStatus != null && (normalizedHttpStatus <= 0 || normalizedHttpStatus >= 500
      || [408, 425, 429].includes(normalizedHttpStatus)))
    || (expectedSessionKey !== null && response.recovery_session_key !== expectedSessionKey)
    || (response.recovery_code === 'CABIN_HOLD_UNAVAILABLE' && !safeHoldRejection)
    || (response.recovery_code === 'CABIN_RESELECT_REQUIRED' && !safeCabinReselection)
    || (response.recovery_code === 'SENIOR_FARE_REJECTED'
      && (bookingAttempted !== true || bookingOutcome !== 'confirmed_failure'
        || retryPolicy !== 'restart_from_beginning' || signals.hasResults))
    || (response.recovery_code === 'MISSING_BOOKING_IDENTIFIERS'
      && (!isMissingIdentifiersRecovery(response) || bookingAttempted !== true
        || bookingOutcome !== 'confirmed_failure' || retryPolicy !== 'restart_from_beginning'
        || response.auto_restart_booking_flow !== true || response.success !== false))
  );

  // 서로 모순되는 계약은 중복 예약 방지를 위해 가장 보수적으로 처리한다.
  const hasExplicitOutcome = bookingOutcome !== '';
  const outcomeClaimsSuccess = bookingOutcome === 'success';
  const outcomeClaimsFailure = hasExplicitOutcome && !outcomeClaimsSuccess;
  const contradictoryContract = invalidRecovery || (signals.canProceedWithBooking && (manualReviewRequired || outcomeUnknown))
    || (signals.canProceedWithBooking && (bookingAttempted === false || outcomeClaimsFailure || resetRequested))
    || (outcomeClaimsSuccess && (!signals.canProceedWithBooking || bookingAttempted === false))
    || (resetRequested && bookingAttempted === false && !safeHoldRejection)
    || (bookingAttempted === true && (
      normalizedHttpStatus === 401
      || AUTH_RETRY_POLICIES.has(retryPolicy)
      || PRECHECK_OUTCOMES.has(bookingOutcome)
      || RETRYABLE_RETRY_POLICIES.has(retryPolicy)
    ));

  if (contradictoryContract) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
      reason: 'contradictory_response_contract',
      persistBlock: true,
      canRestart: false,
      signals,
    };
  }

  // 결과 불확정/수동 확인은 성공·초기화 플래그보다 우선한다.
  if (transportError
    || manualReviewRequired
    || outcomeUnknown
    || MANUAL_REVIEW_RETRY_POLICIES.has(retryPolicy)) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
      reason: transportError ? 'transport_error' : 'manual_review_required',
      persistBlock: true,
      canRestart: false,
      signals,
    };
  }

  if (signals.canProceedWithBooking) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.SUCCESS,
      reason: 'booking_succeeded',
      persistBlock: false,
      canRestart: false,
      signals,
    };
  }

  if (normalizedHttpStatus === 401
    || AUTH_RETRY_POLICIES.has(retryPolicy)
    || bookingOutcome === 'auth_required') {
    return {
      policy: BOOKING_SUBMISSION_POLICY.AUTH_REQUIRED,
      reason: 'authentication_required',
      persistBlock: false,
      canRestart: false,
      signals,
    };
  }

  if (bookingAttempted === false && retryPolicy === 'refresh_booking_session') {
    if (safeCabinReselection) {
      return {
        policy: BOOKING_SUBMISSION_POLICY.RESTART_SAFE,
        reason: 'cabin_reselection_required',
        persistBlock: true, canRestart: true, signals,
      };
    }
    return {
      policy: BOOKING_SUBMISSION_POLICY.RETRYABLE_PRECHECK,
      reason: 'booking_session_refresh_required',
      persistBlock: false,
      canRestart: false,
      requiresSessionRefresh: true,
      signals,
    };
  }

  if (hasCheckedRecovery(response) && response.recovery_code === 'DINING_PREFERENCE_REQUIRED'
    && bookingAttempted === true && bookingOutcome === 'confirmed_failure'
    && retryPolicy === 'correct_booking_inputs' && !resetRequested && !signals.hasResults) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.RETRYABLE_PRECHECK,
      reason: 'correct_booking_inputs', requiresInputCorrection: true,
      persistBlock: false, canRestart: false, signals,
    };
  }

  // 명시적 확정 실패 또는 기존 reset 플래그만 새 세션 재시작을 허용한다.
  if (resetRequested
    && (bookingAttempted !== false || safeHoldRejection)
    && (RESTART_OUTCOMES.has(bookingOutcome) || RESTART_RETRY_POLICIES.has(retryPolicy) || bookingOutcome === '')) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.RESTART_SAFE,
      reason: 'confirmed_failure_safe_to_restart',
      persistBlock: true,
      canRestart: true,
      signals,
    };
  }

  if (bookingAttempted === false
    || PRECHECK_OUTCOMES.has(bookingOutcome)
    || RETRYABLE_RETRY_POLICIES.has(retryPolicy)
    // 구버전 API에서 400/405는 book.pl 호출 전 요청 검증에만 사용된다.
    || ((normalizedHttpStatus === 400 || normalizedHttpStatus === 405) && bookingAttempted !== true)) {
    return {
      policy: BOOKING_SUBMISSION_POLICY.RETRYABLE_PRECHECK,
      reason: 'request_rejected_before_booking',
      persistBlock: false,
      canRestart: false,
      signals,
    };
  }

  return {
    policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
    reason: 'unclassified_booking_failure',
    persistBlock: true,
    canRestart: false,
    signals,
  };
}

export function createBookingSubmissionBlock(decision, {
  scheduleId = null,
  requestId = null,
  expectedSessionKey = '',
  now = Date.now(),
  safeRestartTtlMs = DEFAULT_SAFE_RESTART_TTL_MS,
} = {}) {
  const policy = decision?.policy;
  if (![BOOKING_SUBMISSION_POLICY.RESTART_SAFE, BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW].includes(policy)) {
    return null;
  }

  const blockedAtMs = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  const normalizedExpectedSessionKey = String(expectedSessionKey || '').trim();

  // 안전 재시작은 실패한 요청의 세션을 특정할 수 있어야 한다.
  const effectivePolicy = policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE && !normalizedExpectedSessionKey
    ? BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW
    : policy;

  return {
    version: BOOKING_SUBMISSION_BLOCK_VERSION,
    policy: effectivePolicy,
    reason: policy === effectivePolicy ? decision?.reason || '' : 'missing_expected_session_key',
    blockedAt: new Date(blockedAtMs).toISOString(),
    expiresAt: effectivePolicy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE
      ? new Date(blockedAtMs + Math.max(0, Number(safeRestartTtlMs) || DEFAULT_SAFE_RESTART_TTL_MS)).toISOString()
      : null,
    scheduleId: scheduleId == null ? null : String(scheduleId),
    requestId: String(requestId || '').trim() || null,
    expectedSessionKey: normalizedExpectedSessionKey || null,
    isExpired: false,
  };
}

export function parseBookingSubmissionBlock(raw, { now = Date.now() } = {}) {
  if (!raw) return null;

  let parsed;
  try {
    parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (_) {
    return {
      version: BOOKING_SUBMISSION_BLOCK_VERSION,
      policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
      reason: 'corrupt_submission_block',
      blockedAt: null,
      expiresAt: null,
      requestId: null,
      expectedSessionKey: null,
      isExpired: false,
    };
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  let policy = Object.values(BOOKING_SUBMISSION_POLICY).includes(parsed.policy)
    ? parsed.policy
    : null;
  let expectedSessionKey = String(parsed.expectedSessionKey || parsed.sessionKey || '').trim() || null;

  // 직전 미커밋 포맷을 안전하게 이관한다.
  if (!policy) {
    const legacySafeRestart = parsed.reason === 'confirmed_failure'
      && parsed.manualReviewRequired !== true
      && Boolean(expectedSessionKey);
    policy = legacySafeRestart
      ? BOOKING_SUBMISSION_POLICY.RESTART_SAFE
      : BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW;
  }

  if (![BOOKING_SUBMISSION_POLICY.RESTART_SAFE, BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW].includes(policy)) {
    return null;
  }

  if (policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE && !expectedSessionKey) {
    policy = BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW;
  }

  const blockedAtMs = Date.parse(parsed.blockedAt || '');
  let expiresAt = parsed.expiresAt || null;
  if (policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE && !expiresAt && Number.isFinite(blockedAtMs)) {
    expiresAt = new Date(blockedAtMs + DEFAULT_SAFE_RESTART_TTL_MS).toISOString();
  }
  const expiresAtMs = Date.parse(expiresAt || '');
  const isExpired = policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE
    && Number.isFinite(expiresAtMs)
    && expiresAtMs <= Number(now);
  if (isExpired) {
    // 오래된 확정 실패 기록은 자동/사용자 재시작 근거로 쓰지 않는다.
    policy = BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW;
  }

  return {
    version: BOOKING_SUBMISSION_BLOCK_VERSION,
    policy,
    reason: isExpired
      ? 'safe_restart_expired'
      : (String(parsed.reason || '').trim() || 'booking_submission_blocked'),
    blockedAt: parsed.blockedAt || null,
    expiresAt: isExpired || policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE ? expiresAt : null,
    scheduleId: parsed.scheduleId == null ? null : String(parsed.scheduleId),
    requestId: String(parsed.requestId || '').trim() || null,
    expectedSessionKey,
    isExpired,
  };
}
